import { Badge, Box, Button, Chip, ColorInput, Divider, FileButton, Flex, Grid, Group, Image, Loader, LoadingOverlay, MantineProvider, Select, Switch, Tabs, Text, TextInput, Title, Tooltip, useMantineTheme } from "@mantine/core";
import { generateColors } from "@mantine/colors-generator";
import { IconArrowLeft, IconPhotoPlus } from "@tabler/icons-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MediaApi, TenantApi } from "../../apis";
import { PageLayout } from "../../components/layout";
import { extractColors } from "../../utils/color-extractor";
import { Message } from "../../utils/message";
import { AdvancedTokensForm } from "./components/advanced-tokens-form";
import { CssTokensPanel } from "./components/css-tokens";
import { PaletteTuple } from "./components/palette-tuple";
import { Sketchbook } from "./components/sketchbook";
import classes from "./edit.module.css";

const useBreadcrumb = () => [
  {
    to: "/",
    label: "Эхлэл",
  },
  {
    to: "/tenant/list",
    label: "Tenant жагсаалт",
  },
  {
    label: "Засах",
  },
];

interface TenantData {
  id: string;
  name: string;
  domain: string;
  isActive: boolean;
  logo?: string;
  favicon?: string;
  theme?: any;
}

const TenantEdit = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const breadcrumbs = useBreadcrumb();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tenant, setTenant] = useState<TenantData | null>(null);

  // General fields
  const [name, setName] = useState("");
  const [domain, setDomain] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [showNameInHeader, setShowNameInHeader] = useState(true);
  const [fromEmail, setFromEmail] = useState("");
  const [footerText, setFooterText] = useState("");
  const [defaultLocale, setDefaultLocale] = useState("mn");
  const [supportedLocales, setSupportedLocales] = useState<string[]>(["mn"]);
  const [logoUrl, setLogoUrl] = useState<string | undefined>();
  const [faviconUrl, setFaviconUrl] = useState<string | undefined>();

  // Local blob previews
  const [logoPreview, setLogoPreview] = useState<string | undefined>();
  const [faviconPreview, setFaviconPreview] = useState<string | undefined>();

  // Upload states
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);

  // Theme
  const [primaryColor, setPrimaryColor] = useState("");
  const [advancedTokens, setAdvancedTokens] = useState<Record<string, any>>({});

  // Logo extracted colors
  const [suggestedColors, setSuggestedColors] = useState<string[]>([]);

  const fetchTenant = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await TenantApi.get(id);
      setTenant(data);
      setName(data.name || "");
      setDomain(data.domain || "");
      setIsActive(data.isActive ?? true);
      setShowNameInHeader(data.showNameInHeader ?? true);
      setFromEmail(data.fromEmail || "");
      setFooterText(data.footerText || "");
      setDefaultLocale(data.defaultLocale || "mn");
      setSupportedLocales(data.supportedLocales || ["mn"]);
      setLogoUrl(data.logo || undefined);
      setFaviconUrl(data.favicon || undefined);
      setLogoPreview(undefined);
      setFaviconPreview(undefined);

      if (data.theme) {
        setPrimaryColor(data.theme.primaryColor || "");
        setAdvancedTokens(data.theme.advancedTokens || {});
      }

      if (data.logo) {
        try {
          const colors = await extractColors(data.logo);
          setSuggestedColors(colors);
        } catch {
          // ignore extraction errors
        }
      }
    } catch (err: any) {
      Message.error(err?.message || "Алдаа гарлаа");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchTenant();
  }, [fetchTenant]);

  const handleSaveGeneral = useCallback(async () => {
    if (!id) return;
    setSaving(true);
    try {
      const updates: Record<string, any> = { name, domain, isActive, showNameInHeader, fromEmail: fromEmail || null, footerText: footerText || null, defaultLocale, supportedLocales };
      if (logoUrl !== tenant?.logo) updates.logo = logoUrl || null;
      if (faviconUrl !== tenant?.favicon) updates.favicon = faviconUrl || null;

      await TenantApi.update(id, updates);
      Message.success("Хадгаллаа");
      fetchTenant();
    } catch (err: any) {
      Message.error(err?.message || "Алдаа гарлаа");
    } finally {
      setSaving(false);
    }
  }, [id, name, domain, isActive, showNameInHeader, fromEmail, footerText, logoUrl, faviconUrl, tenant, fetchTenant, defaultLocale, supportedLocales]);

  const handleSaveTheme = useCallback(async () => {
    if (!id) return;
    setSaving(true);
    try {
      await TenantApi.saveTheme(id, { mode: "advanced", primaryColor, advancedTokens });
      Message.success("Theme хадгаллаа");
    } catch (err: any) {
      Message.error(err?.message || "Алдаа гарлаа");
    } finally {
      setSaving(false);
    }
  }, [id, primaryColor, advancedTokens]);

  const handleUploadImage = useCallback(async (blob: File | null, type: "logo" | "favicon") => {
    if (!blob) return;

    const setUploading = type === "logo" ? setUploadingLogo : setUploadingFavicon;
    const setPreview = type === "logo" ? setLogoPreview : setFaviconPreview;
    const setUrl = type === "logo" ? setLogoUrl : setFaviconUrl;

    setUploading(true);
    setPreview(URL.createObjectURL(blob));

    try {
      const form = new FormData();
      form.append("file", blob);
      const res = await MediaApi.uploadImage(form);

      setUrl(res.url);

      if (type === "logo" && blob) {
        try {
          const blobUrl = URL.createObjectURL(blob);
          const colors = await extractColors(blobUrl);
          setSuggestedColors(colors);
          URL.revokeObjectURL(blobUrl);
        } catch {
          // ignore
        }
      }

      Message.success(`${type === "logo" ? "Лого" : "Favicon"} байршууллаа`);
    } catch (err: any) {
      Message.error(err?.message || "Байршуулахад алдаа гарлаа");
      setPreview(undefined);
    } finally {
      setUploading(false);
    }
  }, []);

  const getImageSrc = (preview?: string, savedUrl?: string) => {
    if (preview) return preview;
    if (savedUrl) return savedUrl;
    return undefined;
  };

  if (loading) {
    return (
      <PageLayout title="Tenant засах" breadcrumb={breadcrumbs}>
        <Flex justify="center" align="center" h={300}>
          <Loader />
        </Flex>
      </PageLayout>
    );
  }

  if (!tenant) {
    return (
      <PageLayout title="Tenant олдсонгүй" breadcrumb={breadcrumbs}>
        <Text>Tenant олдсонгүй</Text>
        <Button mt="md" onClick={() => navigate("/tenant/list")}>
          Буцах
        </Button>
      </PageLayout>
    );
  }

  const logoSrc = getImageSrc(logoPreview, logoUrl);
  const faviconSrc = getImageSrc(faviconPreview, faviconUrl);

  return (
    <PageLayout
      title={`Tenant: ${tenant.name}`}
      subTitle={tenant.domain}
      breadcrumb={breadcrumbs}
      extra={[
        <Button key="back" variant="light" leftSection={<IconArrowLeft size={16} />} onClick={() => navigate("/tenant/list")}>
          Буцах
        </Button>,
        <Button
          key="save"
          onClick={async () => {
            await handleSaveGeneral();
            await handleSaveTheme();
          }}
          loading={saving}
        >
          Хадгалах
        </Button>,
      ]}
    >
      <Divider mb="md" />

      <Tabs defaultValue="general">
        <Tabs.List>
          <Tabs.Tab value="general">Ерөнхий</Tabs.Tab>
          <Tabs.Tab value="theme">Theme</Tabs.Tab>
        </Tabs.List>

        {/* General Tab */}
        <Tabs.Panel value="general" pt="md">
          <Grid gap="md">
            {/* Branding card — logo + favicon */}
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Card title="Брэнд" subtitle="Платформын танил тэмдэг — лого болон favicon.">
                <Flex direction="column" gap="lg">
                  <Box>
                    <Text size="sm" fw={500} mb={6}>
                      Лого
                    </Text>
                    <Group gap="md" align="center">
                      <Box className={classes.imageBox} pos="relative">
                        {logoSrc ? (
                          <Image src={logoSrc} w={100} h={100} radius="sm" fit="contain" />
                        ) : (
                          <Flex align="center" justify="center" w={100} h={100} className={classes.placeholder}>
                            <IconPhotoPlus size={24} color="gray" />
                          </Flex>
                        )}
                        <LoadingOverlay visible={uploadingLogo} loaderProps={{ size: "sm" }} />
                      </Box>
                      <Flex direction="column" gap={4}>
                        <FileButton onChange={(file) => handleUploadImage(file, "logo")} accept="image/png,image/jpeg,image/svg+xml">
                          {(props) => (
                            <Button variant="default" size="sm" {...props}>
                              {logoSrc ? "Сольж оруулах" : "Лого сонгох"}
                            </Button>
                          )}
                        </FileButton>
                        <Text size="xs" c="dimmed">
                          PNG/SVG/JPEG · 256×64
                        </Text>
                      </Flex>
                    </Group>
                  </Box>
                  <Box>
                    <Text size="sm" fw={500} mb={6}>
                      Favicon
                    </Text>
                    <Group gap="md" align="center">
                      <Box className={classes.imageBox} pos="relative">
                        {faviconSrc ? (
                          <Image src={faviconSrc} w={48} h={48} radius="sm" fit="contain" />
                        ) : (
                          <Flex align="center" justify="center" w={48} h={48} className={classes.placeholder}>
                            <IconPhotoPlus size={16} color="gray" />
                          </Flex>
                        )}
                        <LoadingOverlay visible={uploadingFavicon} loaderProps={{ size: "xs" }} />
                      </Box>
                      <Flex direction="column" gap={4}>
                        <FileButton onChange={(file) => handleUploadImage(file, "favicon")} accept="image/png,image/jpeg,image/svg+xml,image/x-icon">
                          {(props) => (
                            <Button variant="default" size="sm" {...props}>
                              {faviconSrc ? "Сольж оруулах" : "Favicon сонгох"}
                            </Button>
                          )}
                        </FileButton>
                        <Text size="xs" c="dimmed">
                          Browser tab · 32×32 PNG/ICO
                        </Text>
                      </Flex>
                    </Group>
                  </Box>
                </Flex>
              </Card>
            </Grid.Col>

            {/* Identity card */}
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Card title="Үндсэн мэдээлэл" subtitle="Тенантыг ялгах нэр, домэйн, төлвүүд.">
                <Flex direction="column" gap="md">
                  <TextInput label="Нэр" value={name} onChange={(e) => setName(e.target.value)} required />
                  <TextInput label="Домэйн" value={domain} onChange={(e) => setDomain(e.target.value)} required />
                  <Switch
                    label="Идэвхтэй"
                    description="Тенантыг түр зогсоох бол унтраа."
                    checked={isActive}
                    onChange={(e) => setIsActive(e.currentTarget.checked)}
                  />
                  <Switch
                    label="Header дээр нэр харуулах"
                    description="Лого хажуу талд тенантын нэрийг гаргана."
                    checked={showNameInHeader}
                    onChange={(e) => setShowNameInHeader(e.currentTarget.checked)}
                  />
                </Flex>
              </Card>
            </Grid.Col>

            {/* Communication card */}
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Card title="Харилцаа" subtitle="Системээс гарах email-ийн хаяг ба footer текст.">
                <Flex direction="column" gap="md">
                  <TextInput label="Email илгээгч хаяг" placeholder="noreply@company.mn" value={fromEmail} onChange={(e) => setFromEmail(e.target.value)} />
                  <TextInput label="Footer текст" placeholder="Компаний нэр ХХК" value={footerText} onChange={(e) => setFooterText(e.target.value)} />
                </Flex>
              </Card>
            </Grid.Col>

            {/* Locale card */}
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Card title="Хэлний тохиргоо" subtitle="Тенантын анхдагч хэл болон хэрэглэгчдэд харагдах сонголтууд.">
                <Flex direction="column" gap="md">
                  <Select
                    label="Анхдагч хэл"
                    value={defaultLocale}
                    onChange={(val) => val && setDefaultLocale(val)}
                    data={[
                      { value: "mn", label: "Монгол" },
                      { value: "ko", label: "한국어 (Солонгос)" },
                      { value: "en", label: "English (Англи)" },
                      { value: "zh", label: "中文 (Хятад)" },
                    ]}
                  />
                  <Box>
                    <Text size="sm" fw={500} mb={6}>
                      Дэмжих хэлүүд
                    </Text>
                    <Chip.Group multiple value={supportedLocales} onChange={(val) => setSupportedLocales(val.length > 0 ? val : ["mn"])}>
                      <Group gap="xs">
                        <Chip value="mn">Монгол</Chip>
                        <Chip value="ko">한국어</Chip>
                        <Chip value="en">English</Chip>
                        <Chip value="zh">中文</Chip>
                      </Group>
                    </Chip.Group>
                    <Text size="xs" c="dimmed" mt={6}>
                      2+ хэл сонговол хэрэглэгчдэд хэл сольж болох товч харагдана.
                    </Text>
                  </Box>
                </Flex>
              </Card>
            </Grid.Col>
          </Grid>
        </Tabs.Panel>

        {/* Theme Tab */}
        <Tabs.Panel value="theme" pt="md">
          <ThemeStudio
            primaryColor={primaryColor}
            onPrimaryChange={setPrimaryColor}
            suggestedColors={suggestedColors}
            logoSrc={logoSrc}
            tenantName={tenant.name}
            advancedTokens={advancedTokens}
            onTokenChange={(key, value) => setAdvancedTokens((prev) => ({ ...prev, [key]: value }))}
          />
        </Tabs.Panel>
      </Tabs>
    </PageLayout>
  );
};

export default TenantEdit;

function ThemeStudio({
  primaryColor,
  onPrimaryChange,
  suggestedColors,
  logoSrc,
  tenantName,
  advancedTokens,
  onTokenChange,
}: {
  primaryColor: string;
  onPrimaryChange: (v: string) => void;
  suggestedColors: string[];
  logoSrc?: string;
  tenantName?: string;
  advancedTokens: Record<string, any>;
  onTokenChange: (key: string, value: any) => void;
}) {
  const baseTheme = useMantineTheme();
  const previewTheme = useMemo(() => {
    if (!primaryColor || !primaryColor.startsWith("#")) return baseTheme;
    let tuple: any;
    try {
      tuple = generateColors(primaryColor);
    } catch {
      return baseTheme;
    }
    return {
      ...baseTheme,
      colors: { ...baseTheme.colors, brand: tuple },
    };
  }, [primaryColor, baseTheme]);

  return (
    <MantineProvider theme={previewTheme}>
    <Grid gap="lg">
      {/* LEFT — controls */}
      <Grid.Col span={{ base: 12, md: 4, lg: 3 }}>
        <Flex direction="column" gap="md">
          <Card title="Брэндийн лого">
            {logoSrc ? (
              <Box
                style={{
                  background: "var(--mantine-color-gray-0)",
                  borderRadius: "var(--mantine-radius-md)",
                  padding: "var(--mantine-spacing-md)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 100,
                }}
              >
                <Image src={logoSrc} alt={tenantName || "logo"} fit="contain" h={64} w="auto" mah={64} />
              </Box>
            ) : (
              <Text size="sm" c="dimmed">
                Лого оруулаагүй — Ерөнхий tab-аас нэмнэ үү.
              </Text>
            )}
          </Card>

          {suggestedColors.length > 0 && (
            <Card title="Логоноос гарсан өнгө">
              <Group gap={6}>
                {suggestedColors.slice(0, 6).map((c) => {
                  const active = c.toLowerCase() === (primaryColor || "").toLowerCase();
                  return (
                    <Tooltip key={c} label={c} withArrow>
                      <Box
                        component="button"
                        onClick={() => onPrimaryChange(c)}
                        aria-label={`Сонгох ${c}`}
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          background: c,
                          border: active ? "2px solid var(--mantine-color-dark-9)" : "1px solid var(--mantine-color-gray-3)",
                          cursor: "pointer",
                          padding: 0,
                          boxShadow: active ? "0 0 0 2px var(--mantine-color-white), 0 0 0 4px " + c : "none",
                          transition: "transform 120ms ease",
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </Group>
            </Card>
          )}

          <Card title="Эхэн мөрийн өнгө">
            <ColorInput
              value={primaryColor}
              onChange={onPrimaryChange}
              format="hex"
              swatches={["#25262b", "#868e96", "#fa5252", "#e64980", "#be4bdb", "#7950f2", "#4c6ef5", "#228be6", "#15aabf", "#12b886", "#40c057", "#82c91e", "#fab005", "#fd7e14"]}
            />
          </Card>

          <Card title="Гарсан палитр" badge="10 алхам">
            <PaletteTuple primaryColor={primaryColor} />
          </Card>

          <Card title="CSS токенууд" subtitle="Кодонд ашиглах гол Mantine v9 хувьсагчид.">
            <CssTokensPanel refreshKey={primaryColor} />
          </Card>
        </Flex>
      </Grid.Col>

      {/* RIGHT — live preview */}
      <Grid.Col span={{ base: 12, md: 8, lg: 9 }}>
        <Card title="Mantine компонентуудын урьдчилан харах" subtitle="Энэ хэсэг brand өнгийг сольсон даруйд live шинэчлэгдэнэ.">
          <Sketchbook />
        </Card>
      </Grid.Col>

      {/* FULL WIDTH — advanced design tokens (header / navbar / body / card colors) */}
      <Grid.Col span={12}>
        <Card title="Дэлгэрэнгүй токенууд" subtitle="Header болон хуудасны дэвсгэр, navbar, товч, картны өнгө зэрэг нарийвчилсан тохиргоо.">
          <AdvancedTokensForm values={advancedTokens} onChange={onTokenChange} suggestedColors={suggestedColors} />
        </Card>
      </Grid.Col>
    </Grid>
    </MantineProvider>
  );
}

function Card({ title, subtitle, badge, children }: { title?: string; subtitle?: string; badge?: string; children: React.ReactNode }) {
  return (
    <Box
      style={{
        background: "var(--mantine-color-white)",
        border: "1px solid var(--mantine-color-gray-2)",
        borderRadius: "var(--mantine-radius-md)",
        padding: "var(--mantine-spacing-md)",
      }}
    >
      {(title || badge) && (
        <Group justify="space-between" align="flex-start" mb={subtitle ? 4 : "sm"}>
          {title && (
            <Title order={6} fw={600}>
              {title}
            </Title>
          )}
          {badge && (
            <Badge size="xs" color="brand" variant="light">
              {badge}
            </Badge>
          )}
        </Group>
      )}
      {subtitle && (
        <Text size="xs" c="dimmed" mb="sm">
          {subtitle}
        </Text>
      )}
      {children}
    </Box>
  );
}

